// ── MailCaptureAdminPage — Admin › Integrations › Microsoft 365 Mail ──
// Everything that governs what email gets captured, in one place: the tenant
// connection, which mailboxes are opted in, what never gets captured, and which
// domains belong to which account.
//
// The Test Connection panel is the important one. Graph's Mail.Read application
// permission means every mailbox in the tenant; an Exchange Application Access
// Policy is what narrows it to one security group. The probe deliberately asks
// for a mailbox that should be OFF LIMITS — if it comes back reachable, the
// restriction is not in effect and this page says so in red.
//
// Note the probe reads /messages, not /mailFolders: access policies are scoped
// to Mail.Read and friends, and folder metadata is not on that list, so testing
// a folder call would prove nothing about whether mail itself is protected.
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { Btn, Card, ErrorMsg, FInput, FSelect, Field, SectionHeader, Spinner, StatCard, TabBtn, Td, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

const when = d => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

const STATUS_STYLE = {
  ok:        { fg: "#0F6E56", bg: "#E1F5EE" },
  anchored:  { fg: "#185FA5", bg: "#E6F1FB" },
  resynced:  { fg: "#BA7517", bg: "#FAEEDA" },
  forbidden: { fg: "#993C1D", bg: "#FAECE7" },
  error:     { fg: "#993C1D", bg: "#FAECE7" },
};

// A bare "forbidden" tells you nothing actionable. It nearly always means one
// specific thing — the mailbox is on this list but not in the Exchange security
// group — so say that instead of making someone go and find out.
const STATUS_HINT = {
  forbidden: "Exchange refused. This mailbox is almost certainly missing from the ace-mail-capture "
           + "security group — being on this list is not enough on its own. See doc/m365-add-a-mailbox.md.",
  error: "The sync failed for this mailbox. Hover the Last Sync detail or check the function logs.",
  resynced: "The delta cursor had expired, so it was re-anchored. Run a backfill if you need the gap covered.",
  anchored: "A fresh cursor was created. Incremental sync starts from here.",
  ok: "Last sync completed normally.",
};

const Pill = ({ s }) => {
  const st = STATUS_STYLE[s] || { fg: "#555", bg: "#f0f0f0" };
  return (
    <span title={STATUS_HINT[s] || undefined}
      style={{ color: st.fg, background: st.bg, padding: "2px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600,
               cursor: STATUS_HINT[s] ? "help" : undefined }}>
      {s || "never run"}
    </span>
  );
};

export const MailCaptureAdminPage = () => {
  const { api, role, setConfirm: rawConfirm } = useApp();

  // Close the dialog, THEN act. Without this the modal sits on top of the page
  // while the request runs and the result banner renders underneath it — which
  // looks exactly like the button doing nothing.
  const setConfirm = (opts) => {
    if (!opts) return rawConfirm(null);
    rawConfirm({ ...opts, onConfirm: () => { rawConfirm(null); opts.onConfirm(); } });
  };
  const [cfg, setCfg] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("status");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [test, setTest] = useState(null);
  const [probe, setProbe] = useState("");
  const [secret, setSecret] = useState("");
  const [form, setForm] = useState({});

  const load = () => {
    setLoading(true); setErr(null);
    Promise.all([
      api.get("getMailCaptureConfig"),
      api.get("getSalesAccounts").catch(() => []),
    ]).then(([c, a]) => {
      setCfg(c);
      setAccounts(Array.isArray(a) ? a : []);
      const ic = (c && c.integration && c.integration.config) || {};
      setForm({ tenantid: ic.tenantid || "", clientid: ic.clientid || "" });
      setLoading(false);
    }).catch(e => {
      setErr(/Invalid object name/i.test(e.message)
        ? "Email capture tables are missing — run sql/2026-08-26_email_capture.sql first."
        : e.message);
      setLoading(false);
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // `msg` may be a string, or a function of the response for actions that
  // report back what they did — adding a capture rule also sweeps the Email
  // Review queue, and the count is the whole point of the message.
  const save = (body, msg) => {
    setBusy(true); setNote(null); setErr(null);
    api.post("saveMailCaptureConfig", body)
      .then(r => {
        setBusy(false);
        setNote(typeof msg === "function" ? msg(r || {}) : msg);
        load();
      })
      .catch(e => { setBusy(false); setErr(e.message); });
  };

  const runSync = (backfill) => {
    setBusy(true); setNote(null); setErr(null);
    api.post("mailRunSync", backfill ? { backfill: true } : {})
      .then(r => {
        setBusy(false);
        // Capture off = the engine returns without touching a mailbox. Say so,
        // rather than reporting a sync that never ran.
        if (r.inactive) {
          setNote("Email capture is turned off, so nothing ran. Press Enable first, then Sync Now or Run Full Backfill.");
          load();
          return;
        }
        // Surface an incomplete backfill loudly — a silent partial history is
        // the kind of thing nobody notices until they go looking for an email
        // that should be there.
        const partial = (r.results || [])
          .filter(x => x.backfilled && x.backfilled.complete === false)
          .map(x => `${x.upn}: ${x.backfilled.message}`);
        setNote(
          `Sync complete — ${r.captured} captured, ${r.duplicates} already known, `
          + `${r.skipped} skipped, ${r.queuedUnknown} queued for review`
          + `${r.errors ? `, ${r.errors} error(s)` : ""}.`
          + (partial.length ? "  ⚠️ " + partial.join("  ") : "")
        );
        load();
      })
      .catch(e => { setBusy(false); setErr(e.message); });
  };

  const runTest = () => {
    setBusy(true); setTest(null); setErr(null);
    api.get("mailTestConnection", probe ? { probe } : null)
      .then(r => { setBusy(false); setTest(r); })
      .catch(e => { setBusy(false); setErr(e.message); });
  };

  if (loading) return <Spinner />;
  if (err && !cfg) return <ErrorMsg msg={err} onRetry={load} />;

  const integ = (cfg && cfg.integration) || {};
  const counts = (cfg && cfg.counts) || {};
  const active = !!integ.isactive;
  const canEdit = role.canManageUsers;

  const TABS = ["status", "mailboxes", "exclusions", "domains"];
  // "Capture Rules", not "Never Capture" — the tab holds two different kinds of
  // rule and only one of them is an exclusion. Your own domains sit here to mark
  // what counts as internal, which is not the same as refusing to capture it.
  const tabLabel = t => t === "status" ? "Connection" : t === "mailboxes" ? "Mailboxes"
    : t === "exclusions" ? "Capture Rules" : "Account Domains";

  return <>
    <SectionHeader title="Microsoft 365 Email Capture" actions={[
      <Btn key="t" onClick={runTest} disabled={busy}>Test Connection</Btn>,
      <Btn key="s" onClick={() => runSync(false)} disabled={busy || !active}>{busy ? "Working…" : "Sync Now"}</Btn>,
      canEdit ? <Btn key="a" variant={active ? "danger" : "primary"} disabled={busy}
        onClick={() => setConfirm({
          title: active ? "Turn off email capture" : "Turn on email capture",
          confirmLabel: active ? "Turn Off" : "Turn On",
          variant: active ? "danger" : "primary",
          msg: active
            ? "Stop capturing email? Nothing already captured is deleted."
            : "Start capturing email? Opted-in mailboxes will begin syncing on the next scheduled run.",
          onConfirm: () => save({ action: "setintegration", isactive: !active }, active ? "Capture disabled" : "Capture enabled"),
        })}>{active ? "Disable" : "Enable"}</Btn> : null,
    ]} />

    {note && <div style={{ fontSize: 13, color: "#0F6E56", background: "#E1F5EE", border: "0.5px solid #B9E3D4", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>{note}</div>}
    {err && <div style={{ marginBottom: 12 }}><ErrorMsg msg={err} /></div>}

    <Card>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        <StatCard label="Status" value={active ? "Active" : "Disabled"} sub={"Last run " + when(integ.lastsyncdate)} />
        <StatCard label="Messages Captured" value={counts.totalmessages ?? 0} />
        <StatCard label="Matched to a Person" value={counts.matched ?? 0} />
        <StatCard label="Account Only" value={counts.accountonly ?? 0} />
        <StatCard label="Unmatched" value={counts.unmatched ?? 0} />
        <StatCard label="Awaiting Review" value={counts.queuedaddresses ?? 0} />
      </div>
    </Card>

    {test && (
      <div style={{ marginTop: 16 }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Connection test</div>

          {/* Lead with the real problem. Every mailbox failing because one field
              is blank is not four problems, it's one. */}
          {test.configured === false && (
            <div style={{ padding: "10px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13,
                          background: "#E6F1FB", border: "0.5px solid #B9D4EE", color: "#185FA5" }}>
              Not configured yet — enter the Directory (tenant) ID and Application (client) ID below and save
              the client secret. Until then nothing reaches Microsoft, so these results say nothing about
              whether your setup is correct.
            </div>
          )}

          {/* The security gate. Three states, deliberately: a check that reports
              "pass" when it simply couldn't run is worse than no check at all. */}
          {test.scopeProbe && (() => {
            const r = test.scopeProbe.result;
            const tone = r === "pass" ? { bg: "#E1F5EE", bd: "#B9E3D4", fg: "#0F6E56", mark: "✓" }
                       : r === "fail" ? { bg: "#FAECE7", bd: "#E8B4A8", fg: "#993C1D", mark: "⚠️" }
                       :                { bg: "#FAEEDA", bd: "#EBD3A0", fg: "#7a5200", mark: "—" };
            return (
              <div style={{ padding: "10px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13,
                            background: tone.bg, border: "0.5px solid " + tone.bd, color: tone.fg }}>
                <strong>{tone.mark} Scope probe · {test.scopeProbe.upn}</strong>
                <div style={{ marginTop: 4 }}>{test.scopeProbe.verdict}</div>
                {(test.scopeProbe.detail || test.scopeProbe.error) && (
                  <div style={{ marginTop: 4, opacity: 0.85, fontSize: 12 }}>
                    {test.scopeProbe.detail || test.scopeProbe.error}
                  </div>
                )}
              </div>
            );
          })()}
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Mailbox</Th><Th>Reachable</Th><Th>Detail</Th></tr></thead>
            <tbody>{(test.mailboxes || []).map(m => (
              <tr key={m.upn}>
                <Td>{m.upn}</Td>
                <Td>{m.reachable
                  ? <span style={{ color: "#0F6E56", fontWeight: 600 }}>Yes</span>
                  : <span style={{ color: "#993C1D", fontWeight: 600 }}>No</span>}</Td>
                <Td style={{ color: "var(--color-text-secondary)" }}>
                  {m.reachable ? (m.totalItemCount != null ? m.totalItemCount + " items in Inbox" : "—") : (m.error || "—")}
                </Td>
              </tr>
            ))}</tbody>
          </table>
          {test.policyError && <div style={{ fontSize: 12, color: "#993C1D", marginTop: 10 }}>Policy problem: {test.policyError}</div>}
        </Card>
      </div>
    )}

    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 3, position: "relative", zIndex: 1, marginTop: 4, borderBottom: "4px solid " + B.cyan }}>
        {TABS.map(t => <TabBtn key={t} id={t} current={tab} label={tabLabel(t)} onClick={setTab} />)}
      </div>
      <div style={{ background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderTop: "none", borderRadius: "0 0 8px 8px", padding: 16, position: "relative", zIndex: 2 }}>

        {tab === "status" && (
          <div style={{ maxWidth: 620 }}>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 14 }}>
              From the app registration in Entra. The client secret is written straight to Key Vault as
              <code style={{ fontFamily: "var(--font-mono)" }}> m365mail-clientsecret</code> and is never stored in SQL or shown again.
            </div>
            <Field label="Directory (tenant) ID">
              <FInput value={form.tenantid} onChange={v => setForm(f => ({ ...f, tenantid: v }))} disabled={!canEdit} />
            </Field>
            <Field label="Application (client) ID">
              <FInput value={form.clientid} onChange={v => setForm(f => ({ ...f, clientid: v }))} disabled={!canEdit} />
            </Field>
            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
              <Btn variant="primary" disabled={!canEdit || busy}
                onClick={() => save({ action: "setintegration", tenantid: form.tenantid, clientid: form.clientid }, "Connection saved")}>Save IDs</Btn>
            </div>

            <Field label="Client secret (paste to replace)">
              <FInput type="password" value={secret} onChange={setSecret} placeholder="••••••••" disabled={!canEdit} />
            </Field>
            <Btn variant="primary" disabled={!canEdit || busy || !secret}
              onClick={() => { save({ action: "setsecret", secret }, "Secret written to Key Vault"); setSecret(""); }}>Save Secret</Btn>

            <div style={{ marginTop: 24, paddingTop: 16, borderTop: "0.5px solid var(--color-border-tertiary)" }}>
              <Field label="Scope probe — a mailbox that should NOT be readable">
                <FInput value={probe} onChange={setProbe} placeholder="someone-outside-the-group@yourdomain.com" />
              </Field>
              <Btn onClick={runTest} disabled={busy}>Run Connection Test</Btn>
            </div>
          </div>
        )}

        {tab === "mailboxes" && (
          <MailboxesTab cfg={cfg} canEdit={canEdit} busy={busy} active={active} save={save}
            onBackfill={() => runSync(true)} setConfirm={setConfirm} />
        )}

        {tab === "exclusions" && (
          <ExclusionsTab cfg={cfg} canEdit={canEdit} busy={busy} save={save} setConfirm={setConfirm} />
        )}

        {tab === "domains" && (
          <DomainsTab cfg={cfg} accounts={accounts} canEdit={canEdit} busy={busy} save={save} setConfirm={setConfirm} />
        )}

      </div>
    </div>
  </>;
};

// ── Mailboxes ────────────────────────────────────────────────────────────────
// How far back the one-time history pull reaches, editable in place. Commits on
// blur or Enter, and only when the value actually changed — nobody wants a save
// fired because they tabbed through a field.
//
// Turns amber above 30 days on a mailbox that hasn't been backfilled yet: a
// large window on a busy mailbox hits the per-run page cap, and the result is a
// partial history rather than an error.
const DaysCell = ({ m, canEdit, busy, save }) => {
  const current = m.backfilldays == null ? 90 : Number(m.backfilldays);
  const [v, setV] = useState(String(current));
  useEffect(() => { setV(String(current)); }, [current]);

  const risky = !m.backfilledat && current > 30;
  const hint = risky
    ? "Large window on a mailbox that hasn't been backfilled yet — may exceed the per-run page cap and pull only partial history."
    : undefined;

  if (!canEdit) {
    return <span title={hint} style={{ color: risky ? "#BA7517" : undefined, fontWeight: risky ? 600 : 400 }}>{current}</span>;
  }

  const commit = () => {
    const n = Math.max(1, Math.min(3650, Math.round(Number(v) || 0)));
    if (n === current) { setV(String(current)); return; }
    setV(String(n));
    save({ action: "setmailbox", id: m.id, backfilldays: n }, `Backfill window for ${m.upn} set to ${n} days`);
  };

  return (
    <input type="number" min={1} max={3650} value={v} disabled={busy} title={hint}
      onChange={e => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
      style={{
        width: 68, padding: "4px 6px", fontSize: 13, borderRadius: 4,
        border: "1px solid " + (risky ? "#EBD3A0" : "#999"),
        background: risky ? "#FAEEDA" : "#fff",
        color: risky ? "#7a5200" : "#1a1a1a",
        fontWeight: risky ? 600 : 400,
        textAlign: "right",
      }} />
  );
};

// Three states, and the middle one is the useful addition: a backfill that ran
// out of pages is no longer just "pending" — it shows how far back it walked
// and how much of the window is left, so pressing the button again is an
// informed choice rather than a shot in the dark.
const BackfillCell = ({ m }) => {
  const day = d => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });

  if (m.backfilledat) {
    return <span title={`${m.backfillcaptured || 0} messages captured by the history pull`}>
      {when(m.backfilledat)}
    </span>;
  }
  if (m.backfilledthrough) {
    return (
      <span style={{ color: "#BA7517", fontWeight: 600 }}
        title={`Reached ${day(m.backfilledthrough)}. Run the backfill again to continue from there — already-captured mail is skipped. ${m.backfillcaptured || 0} captured so far.`}>
        part-way · back to {day(m.backfilledthrough)}
      </span>
    );
  }
  return <span style={{ color: "#BA7517" }}>pending</span>;
};

const MailboxesTab = ({ cfg, canEdit, busy, active, save, onBackfill, setConfirm }) => {
  const [upn, setUpn] = useState("");
  const [days, setDays] = useState("90");
  const rows = (cfg && cfg.mailboxes) || [];

  return <>
    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12 }}>
      Only mailboxes listed here with capture on are ever read, regardless of what Exchange would allow.
    </div>

    {/* The step people miss. It was a footnote before and got missed anyway. */}
    <div style={{ fontSize: 12, background: "#E6F1FB", border: "0.5px solid #B9D4EE", color: "#185FA5",
                  borderRadius: 8, padding: "8px 12px", marginBottom: 14 }}>
      <strong>Adding a mailbox here is only half of it.</strong> The person must also be a member of the
      <code style={{ fontFamily: "var(--font-mono)" }}> ace-mail-capture </code>
      security group in Exchange, or Microsoft refuses the request and the row shows
      <strong> forbidden</strong>. Two gates on purpose: this one stops the app asking, that one stops
      Microsoft answering. Steps are in <code style={{ fontFamily: "var(--font-mono)" }}>doc/m365-add-a-mailbox.md</code>.
    </div>

    {/* Call it out on the row itself, not just in the preamble. */}
    {rows.some(m => m.isactive && m.lastsyncstatus === "forbidden") && (
      <div style={{ fontSize: 12, background: "#FAECE7", border: "0.5px solid #E8B4A8", color: "#993C1D",
                    borderRadius: 8, padding: "8px 12px", marginBottom: 14 }}>
        {rows.filter(m => m.isactive && m.lastsyncstatus === "forbidden").map(m => m.upn).join(", ")}
        {" "}— Exchange is refusing these. Add them to the <code>ace-mail-capture</code> group, wait a few
        minutes, then Sync Now:
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, marginTop: 6, userSelect: "all",
                      whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
          {"Connect-ExchangeOnline\n"}
          {rows.filter(m => m.isactive && m.lastsyncstatus === "forbidden")
               .map(m => `Add-DistributionGroupMember -Identity ace-mail-capture -Member ${m.upn}`)
               .join("\n")}
        </div>
      </div>
    )}

    {canEdit && (
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 280 }}><Field label="Mailbox (UPN)"><FInput value={upn} onChange={setUpn} placeholder="rep@yourdomain.com" /></Field></div>
        <div style={{ width: 130 }}><Field label="Backfill days"><FInput value={days} onChange={setDays} /></Field></div>
        <div style={{ marginBottom: 14 }}>
          <Btn variant="primary" disabled={busy || !upn.includes("@")}
            onClick={() => { save({ action: "addmailbox", upn, backfilldays: Number(days) || 90 }, "Mailbox added"); setUpn(""); }}>Add Mailbox</Btn>
        </div>
      </div>
    )}

    <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
      <thead><tr>
        <Th>Mailbox</Th><Th>Capture</Th><Th>Status</Th><Th>Folders</Th>
        <Th>Backfill Days</Th><Th>Backfilled</Th><Th>Last Sync</Th><Th></Th>
      </tr></thead>
      <tbody>{rows.map(m => (
        <tr key={m.id}>
          <Td style={{ fontWeight: 500 }}>{m.upn}</Td>
          <Td>{m.isactive
            ? <span style={{ color: "#0F6E56", fontWeight: 600 }}>On</span>
            : <span style={{ color: "var(--color-text-secondary)" }}>Off</span>}</Td>
          <Td><Pill s={m.lastsyncstatus} /></Td>
          {/* Delta counter only — the history pull is reported separately in the
              next column, because conflating the two made 736 captured messages
              show up here as "inbox (50)". */}
          <Td style={{ fontSize: 12, color: "var(--color-text-secondary)" }}
              title="Messages seen by the incremental sync since this mailbox was anchored. The history pull is counted under Backfilled.">
            {(m.folders || []).length === 0 ? "not started"
              : (m.folders || []).map(f => f.folder + " (" + f.messagesseen + ")").join(", ")}
          </Td>
          <Td><DaysCell m={m} canEdit={canEdit} busy={busy} save={save} /></Td>
          <Td><BackfillCell m={m} /></Td>
          <Td>{when(m.lastsyncutc)}</Td>
          <Td>{canEdit && (
            <div style={{ display: "flex", gap: 6 }}>
              <Btn disabled={busy} onClick={() => save({ action: "setmailbox", id: m.id, isactive: !m.isactive }, m.isactive ? "Capture off" : "Capture on")}>
                {m.isactive ? "Turn Off" : "Turn On"}
              </Btn>
              <Btn disabled={busy} onClick={() => setConfirm({
                title: "Reset sync cursor",
                confirmLabel: "Reset Cursor",
                variant: "primary",
                msg: "Reset the sync cursor for " + m.upn + "? The next run starts a fresh cursor anchored at that "
                   + "moment. Nothing already captured is deleted — but anything that arrived since the last "
                   + "successful sync and hasn't been picked up yet will be missed by the incremental sync. "
                   + "Run a backfill afterwards to cover the gap by date.",
                onConfirm: () => save({ action: "resetdelta", id: m.id }, "Cursor reset"),
              })}>Reset Cursor</Btn>
            </div>
          )}</Td>
        </tr>
      ))}</tbody>
      {rows.length === 0 && <tbody><tr><Td colSpan={8} style={{ color: "var(--color-text-secondary)" }}>No mailboxes opted in yet.</Td></tr></tbody>}
    </table>

    {canEdit && rows.length > 0 && (
      <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10 }}>
        <Btn disabled={busy || !active} onClick={() => setConfirm({
          title: "Run full backfill",
          confirmLabel: "Run Backfill",
          variant: "primary",
          msg: "Pull history for every active mailbox? This reads mail only — nothing is deleted or changed in Exchange. "
             + "It can take several minutes.",
          onConfirm: onBackfill,
        })}>Run Full Backfill</Btn>
        {!active && (
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            Turn capture on first — the sync engine does nothing while it's disabled.
          </span>
        )}
      </div>
    )}
  </>;
};

// ── Exclusions ───────────────────────────────────────────────────────────────
// A capture rule stops future mail; the backend also closes out the Email
// Review rows the rule now covers. Say how many, and flag the ones it left
// alone — a resolved address is a real Contact somebody linked on purpose, so
// the sweep won't touch it and you may want to go unlink it yourself.
const ruleNote = (r) => {
  // No sweep ran — disabling a rule, or an older API build. Don't claim a
  // result that wasn't reported.
  if (r.swept === undefined || r.swept === null) return "Rule updated.";
  const n = Number(r.swept) || 0;
  const c = Number(r.resolvedConflicts) || 0;
  let s = n === 0
    ? "Rule saved. No addresses in Email Review matched it."
    : `Rule saved — ${n} address${n === 1 ? "" : "es"} in Email Review excluded.`;
  if (c) s += ` ${c} already linked to a contact or lead ${c === 1 ? "was" : "were"} left alone.`;
  return s;
};

// Result of the retroactive sweep button. Same shape as ruleNote, different
// wording — nothing was added, existing rules were just applied to the backlog.
const sweepNote = (r) => {
  const n = Number(r.swept) || 0;
  const c = Number(r.resolvedConflicts) || 0;
  let s = n === 0
    ? "Nothing left to exclude — Email Review has no addresses matching your rules."
    : `${n} address${n === 1 ? "" : "es"} excluded from Email Review.`;
  if (c) s += ` ${c} already linked to a contact or lead ${c === 1 ? "was" : "were"} left alone — check those, since no new mail from them will reach a timeline.`;
  return s;
};

const ExclusionsTab = ({ cfg, canEdit, busy, save, setConfirm }) => {
  const [kind, setKind] = useState("domain");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const rows = (cfg && cfg.exclusions) || [];

  const internal = rows.filter(r => r.kind === "internal_domain");
  const excluded = rows.filter(r => r.kind !== "internal_domain");
  // null means the hardening migration hasn't run; [] means it ran and found none.
  const internalContacts = cfg ? cfg.internalcontacts : undefined;

  const KIND_LABEL = { domain: "Domain", address: "Address" };

  const Table = ({ data, valueHeader }) => (
    <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", marginBottom: 8 }}>
      <thead><tr><Th>{valueHeader}</Th><Th>Reason</Th><Th>Active</Th><Th>In Review</Th><Th></Th></tr></thead>
      <tbody>{data.map(r => (
        <tr key={r.id}>
          <Td style={{ fontWeight: 500 }}>
            {r.kind !== "internal_domain" && (
              <span style={{ fontSize: 11, color: "var(--color-text-secondary)", marginRight: 8 }}>{KIND_LABEL[r.kind]}</span>
            )}
            {r.value}
          </Td>
          <Td style={{ color: "var(--color-text-secondary)" }}>{r.reason || "—"}</Td>
          <Td>{r.isactive ? "Yes" : "No"}</Td>
          {/* Addresses still sitting in Email Review that this rule covers.
              Non-zero means the rule was added after they were captured. */}
          <Td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums",
                       color: r.queuematches ? "#7a5200" : "var(--color-text-secondary)",
                       fontWeight: r.queuematches ? 600 : 400 }}>
            {r.queuematches || "—"}
          </Td>
          <Td>{canEdit && (
            <div style={{ display: "flex", gap: 6 }}>
              {r.isactive && r.queuematches > 0 && (
                <Btn variant="primary" disabled={busy} onClick={() => setConfirm({
                  title: "Exclude matching addresses",
                  confirmLabel: "Exclude " + r.queuematches,
                  msg: `Exclude the ${r.queuematches} address${r.queuematches === 1 ? "" : "es"} in Email Review `
                     + `covered by ${r.value}? They leave the review queue. Mail already captured is kept, and `
                     + `addresses already linked to a contact or lead are left alone.`,
                  onConfirm: () => save({ action: "sweepqueue", id: r.id }, sweepNote),
                })}>Exclude {r.queuematches}</Btn>
              )}
              <Btn disabled={busy} onClick={() => save({ action: "setexclusion", id: r.id, isactive: !r.isactive }, ruleNote)}>
                {r.isactive ? "Disable" : "Enable"}
              </Btn>
              <Btn variant="danger" disabled={busy} onClick={() => setConfirm({
                title: "Delete capture rule",
                msg: r.kind === "internal_domain"
                  ? "Delete this internal domain? Mail to and from it will start being treated as external — including your own staff's internal threads."
                  : "Delete this rule? Future mail matching it will start being captured.",
                onConfirm: () => save({ action: "deleteexclusion", id: r.id }, "Rule deleted"),
              })}>Delete</Btn>
            </div>
          )}</Td>
        </tr>
      ))}</tbody>
      {data.length === 0 && <tbody><tr><Td colSpan={5} style={{ color: "var(--color-text-secondary)" }}>None.</Td></tr></tbody>}
    </table>
  );

  // Backlog: addresses captured BEFORE the rule that now covers them existed.
  // Adding a rule sweeps forward automatically, so a non-zero count here means
  // rules that predate that behaviour, or a sync that queued new addresses.
  const sweep = (cfg && cfg.queuesweep) || null;
  const backlog = sweep ? Number(sweep.sweepable) || 0 : 0;
  const stranded = sweep ? Number(sweep.resolvedConflicts) || 0 : 0;

  return <>
    {canEdit && backlog > 0 && (
      <div style={{ marginBottom: 16, padding: "12px 14px", borderRadius: 8,
                    background: "#FAEEDA", border: "0.5px solid #EBD3A0", fontSize: 13, color: "#7a5200" }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          {backlog} address{backlog === 1 ? "" : "es"} in Email Review {backlog === 1 ? "is" : "are"} already covered by your rules
        </div>
        <div style={{ marginBottom: 10 }}>
          These were captured before the rule that covers them existed. Excluding them clears the review
          queue in one pass. Mail already captured is kept — this only stops them being asked about.
          {stranded > 0 && ` ${stranded} more ${stranded === 1 ? "is" : "are"} linked to a contact or lead and will be left alone.`}
        </div>
        <Btn variant="primary" disabled={busy} onClick={() => setConfirm({
          title: "Exclude matching addresses",
          confirmLabel: "Exclude " + backlog,
          msg: `Exclude all ${backlog} address${backlog === 1 ? "" : "es"} in Email Review that your active rules `
             + `already cover? They leave the review queue. Mail already captured is kept, and addresses linked `
             + `to a contact or lead are left alone.`,
          onConfirm: () => save({ action: "sweepqueue" }, sweepNote),
        })}>Exclude All {backlog} Matching</Btn>
      </div>
    )}

    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Your domains</div>
    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 10 }}>
      These mark which addresses are <em>us</em>. They do <strong>not</strong> stop your own mail from being
      captured — an email you send to a borrower still is, and shows as <strong>Sent</strong>. What they do is
      drop a message when <em>nothing external is left</em> after removing them, so staff-to-staff threads are
      never written to the database. They also decide whether a captured message reads as Sent or Received.
    </div>

    {/* ── ADD A RULE ───────────────────────────────────────────────────────────
        Sits at the top rather than the bottom. It used to live after both
        tables, which meant every single rule cost a scroll to the end of a page
        whose length grows with the number of rules already on it — the one
        control you use repeatedly was the hardest one to reach, and got harder
        the more you used it.

        It is boxed, and the heading says "any of the three", because its
        position now puts it INSIDE the "Your domains" section. Without that it
        would read as "add an internal domain", which is only one of the three
        things this form does — and the wrong one to guess, since picking it by
        mistake tells the system an outside company is you.

        The form covers every rule kind, so it deliberately does NOT sit under
        one section's heading as if it belonged to it. */}
    {canEdit && (
      <div style={{ marginBottom: 18, padding: "12px 14px", borderRadius: 8,
                    background: "var(--color-background-secondary)",
                    border: "0.5px solid var(--color-border-tertiary)" }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Add a rule</div>
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 10 }}>
          Adds any of the three kinds below — pick which in <strong>Type</strong>.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ width: 210 }}><Field label="Type"><FSelect value={kind} onChange={setKind} options={[
            { value: "domain", label: "Never capture — domain" },
            { value: "address", label: "Never capture — address" },
            { value: "internal_domain", label: "One of your domains" },
          ]} /></Field></div>
          <div style={{ minWidth: 240 }}><Field label={kind === "address" ? "Address" : "Domain"}>
            <FInput value={value} onChange={setValue} placeholder={kind === "address" ? "vendor@example.com" : "example.com"} />
          </Field></div>
          <div style={{ minWidth: 200 }}><Field label="Reason"><FInput value={reason} onChange={setReason} /></Field></div>
          <div style={{ marginBottom: 14 }}>
            <Btn variant="primary" disabled={busy || !value}
              onClick={() => { save({ action: "addexclusion", kind, value, reason }, ruleNote); setValue(""); setReason(""); }}>Add Rule</Btn>
          </div>
        </div>
      </div>
    )}

    <Table data={internal} valueHeader="Internal domain" />

    {/* Contacts or leads someone has created holding a company address. These
        cannot expose anything — matching skips internal addresses and a DB
        trigger blocks the link — but they should not sit there unnoticed. */}
    {Array.isArray(internalContacts) && internalContacts.length > 0 && (
      <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8,
                    background: "#FAEEDA", border: "0.5px solid #EBD3A0", fontSize: 12, color: "#7a5200" }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {internalContacts.length} CRM record{internalContacts.length === 1 ? "" : "s"} use one of your own email addresses
        </div>
        <div style={{ marginBottom: 8 }}>
          No email is linked to these and none can be — internal addresses are excluded from matching and the
          database refuses the link outright. Listed here so an accidental (or deliberate) one doesn't go unseen.
        </div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {internalContacts.slice(0, 20).map((c, i) => (
            <li key={i}>{c.entitytype} · {c.displayname || "(no name)"} · {c.email}</li>
          ))}
        </ul>
        {internalContacts.length > 20 && <div style={{ marginTop: 6 }}>…and {internalContacts.length - 20} more.</div>}
      </div>
    )}
    {internalContacts === null && (
      <div style={{ marginTop: 12, fontSize: 12, color: "var(--color-text-secondary)" }}>
        Run <code>sql/2026-08-26_email_capture_hardening.sql</code> to enable the internal-address check.
      </div>
    )}

    <div style={{ fontSize: 13, fontWeight: 600, marginTop: 20, marginBottom: 6 }}>Never capture</div>
    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 10 }}>
      Third parties you never want recorded — payroll, benefits, SaaS notifications, your own bank. These are
      stripped from the address list, and a message whose <em>sender</em> matches one is dropped outright even
      if a borrower is copied on it.
    </div>
    <Table data={excluded} valueHeader="Domain or address" />
  </>;
};

// ── Account domains ──────────────────────────────────────────────────────────
const DomainsTab = ({ cfg, accounts, canEdit, busy, save, setConfirm }) => {
  const [accountid, setAccountid] = useState("");
  const [domain, setDomain] = useState("");
  const rows = (cfg && cfg.accountdomains) || [];

  const opts = [{ value: "", label: "— Select an account —" },
    ...accounts.map(a => ({ value: a.id, label: a.companyname || a.id }))];

  return <>
    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12 }}>
      Maps an email domain to an account, so mail from someone you've never met still lands on the right
      company. Adding one re-matches everything already captured. Domains marked <em>derived</em> were
      inferred from existing contact emails at install time.
    </div>

    {canEdit && (
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 280 }}><Field label="Account"><FSelect value={accountid} onChange={setAccountid} options={opts} /></Field></div>
        <div style={{ minWidth: 220 }}><Field label="Domain"><FInput value={domain} onChange={setDomain} placeholder="borrowercompany.com" /></Field></div>
        <div style={{ marginBottom: 14 }}>
          <Btn variant="primary" disabled={busy || !accountid || !domain}
            onClick={() => { save({ action: "adddomain", accountid, domain }, "Domain mapped and history re-matched"); setDomain(""); }}>Map Domain</Btn>
        </div>
      </div>
    )}

    <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
      <thead><tr><Th>Domain</Th><Th>Account</Th><Th>Source</Th><Th></Th></tr></thead>
      <tbody>{rows.map(r => (
        <tr key={r.id}>
          <Td style={{ fontWeight: 500 }}>{r.domain}</Td>
          <Td>{r.companyname || r.accountid}</Td>
          <Td style={{ color: "var(--color-text-secondary)" }}>{r.source}</Td>
          <Td>{canEdit && (
            <Btn variant="danger" disabled={busy} onClick={() => setConfirm({
              title: "Unmap domain",
              confirmLabel: "Unmap",
              msg: "Unmap " + r.domain + "? Existing links stay; new mail from this domain won't auto-attach.",
              onConfirm: () => save({ action: "deletedomain", id: r.id }, "Domain unmapped"),
            })}>Delete</Btn>
          )}</Td>
        </tr>
      ))}</tbody>
      {rows.length === 0 && <tbody><tr><Td colSpan={4} style={{ color: "var(--color-text-secondary)" }}>No domains mapped.</Td></tr></tbody>}
    </table>
  </>;
};
